<template>
  <div class="data-table-container">
    <v-data-table
      :headers="processedHeaders"
      :items="items"
      :items-per-page="computedItemsPerPage"
      :loading="loading"
      class="data-table"
      hide-default-footer
      :disable-sort="!enableSorting"
    >
      <!-- No data template -->
      <template #no-data>
        <div class="text-center pa-5">
          <p class="text-body-1 mb-2">{{ noDataText || 'No data found' }}</p>
          <p class="text-caption grey--text">{{ noDataSubtext || 'Check back later' }}</p>
        </div>
      </template>

      <!-- Dynamic slot for each column -->
      <template v-for="header in processedHeaders" #[`item.${header.key}`]="{ item }">
        <slot :name="`column-${header.key}`" :item="item" :value="item[header.key]">
          {{ item[header.key] }}
        </slot>
      </template>

      <!-- Action column if needed -->
      <template v-if="hasActions" #[`item.action`]="{ item }">
        <slot name="column-action" :item="item"></slot>
      </template>
    </v-data-table>

    <!-- Pagination -->
    <div v-if="showPagination" class="d-flex justify-space-between align-center pa-4">
      <div class="text-caption grey--text">
        Page {{ currentPage }} of {{ totalPages }}
      </div>
      <v-pagination
        :model-value="currentPage"
        @update:modelValue="onPageUpdate"
        :length="totalPages"
        :disabled="loading"
        :total-visible="7"
      ></v-pagination>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DataTable',
  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    itemsPerPage: {
      type: Number,
      default: 5
    },
    hasActions: {
      type: Boolean,
      default: false
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    currentPage: {
      type: Number,
      default: 1
    },
    totalPages: {
      type: Number,
      default: 1
    },
    enableSorting: {
      type: Boolean,
      default: true
    },
    noDataText: {
      type: String,
      default: ''
    },
    noDataSubtext: {
      type: String,
      default: ''
    }
  },
  computed: {
    processedHeaders() {
      // Map legacy header fields (text, value) to Vuetify 3 fields (title, key)
      return this.headers.map(h => ({
        // If 'title' exists use it, otherwise fall back to 'text'
        title: h.title || h.text || '',
        // Vuetify 3 uses 'key' instead of 'value'
        key: h.key || h.value,
        align: h.align || undefined,
        sortable: typeof h.sortable !== 'undefined' ? h.sortable : true,
        width: h.width || undefined,
      }));
    },
    computedItemsPerPage() {
      // When pagination UI is hidden, show all rows
      return this.showPagination ? this.itemsPerPage : -1;
    }
  },
  methods: {
    onPageUpdate(page) {
      // Emit custom event for parent components listening to 'page-change'
      this.$emit('page-change', page);
      // Also emit the standard v-model update event so parent can use v-model or .sync if desired
      this.$emit('update:currentPage', page);
    }
  }
};
</script>

<style scoped>
.data-table-container {
  width: 100%;
}

.data-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

/* Custom styles for the table that match the image */
:deep(.v-data-table) {
  background-color: transparent !important;
  box-shadow: none !important;
}

/* Ensure proper spacing between rows using border-spacing instead of flex */
:deep(.v-data-table table) {
  width: 100%;
  border-collapse: separate !important; /* allow spacing */
  border-spacing: 0 12px !important;   /* vertical gap of 12px between rows */
  background-color: #ffffff !important; /* make gap background white */
}

:deep(.v-data-table-header) {
  background-color: transparent !important;
}

:deep(.v-data-table-header th) {
  border-bottom: none !important;
  font-weight: 500 !important;
  color: rgba(0, 0, 0, 0.6) !important;
}

/* Row styles */
:deep(.v-data-table tbody tr) {
  display: table-row !important;  /* restore native table row display */
  background-color: #f0f7fa !important;
  border-radius: 8px !important;
  border: none !important; /* remove default borders to avoid grey lines */
  transition: all 0.3s;
}

:deep(.v-data-table tbody tr:hover) {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1) !important;
}

/* Cell styles */
:deep(.v-data-table tbody td) {
  display: table-cell !important; /* restore native table cell */
  border-bottom: none !important;
  padding: 6px 24px !important; /* reduced vertical padding to shorten row height */
}

:deep(.v-data-table tbody tr td:first-child) {
  border-top-left-radius: 8px;
  border-bottom-left-radius: 8px;
}

:deep(.v-data-table tbody tr td:last-child) {
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
}

:deep(.v-data-table-footer) {
  background-color: transparent !important;
}
</style> 